import React from 'react';
import { useTheme } from '../../ThemeContext';
import { detectDriver } from '../../utils/driverDetection';

interface DestinationConnectionFormProps {
  targetType: 'jdbc' | 'excel_directory' | 'csv_directory' | 'parquet_directory' | 'azure_blob';
  formData: any;
  onFormDataChange: (updates: any) => void;
  onUrlChange: (value: string) => void;
  required?: boolean;
}

export const DestinationConnectionForm: React.FC<DestinationConnectionFormProps> = ({
  targetType,
  formData,
  onFormDataChange,
  onUrlChange,
  required = false,
}) => {
  const { theme } = useTheme();
  const targetSuggestion = targetType === 'jdbc' ? detectDriver(formData.targetUrl || '') : null;

  const applyTargetDetected = () => {
    if (targetSuggestion) {
      onFormDataChange({
        targetDriver: targetSuggestion.driver,
        targetDriverUrl: targetSuggestion.driver_url
      });
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
          Target Type
        </label>
        <select
          value={targetType}
          onChange={(e) => onFormDataChange({ targetType: e.target.value as any })}
          style={{
            width: '100%',
            padding: 8,
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: 6,
            fontSize: 13,
            background: theme.inputBackground,
            color: theme.text
          }}
        >
          <option value="jdbc">JDBC</option>
          <option value="excel_directory">Excel directory</option>
          <option value="csv_directory">CSV directory</option>
          <option value="parquet_directory">Parquet directory</option>
          <option value="azure_blob">Azure Blob Storage</option>
        </select>
      </div>

      {targetType === 'jdbc' && (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              JDBC URL {required && '*'}
            </label>
            <input
              type="text"
              required={required && targetType === 'jdbc'}
              value={formData.targetUrl || ''}
              onChange={(e) => onUrlChange(e.target.value)}
              style={{
                width: '100%',
                padding: 8,
                border: `1px solid ${theme.inputBorder}`,
                borderRadius: 6,
                fontSize: 13,
                background: theme.inputBackground,
                color: theme.text
              }}
            />
            {targetSuggestion && (
              <div style={{ marginTop: 6, fontSize: 12, color: theme.textSecondary }}>
                Detected driver: <code style={{ background: theme.codeBackground, padding: '2px 6px', borderRadius: 4, color: theme.text }}>
                  {targetSuggestion.driver}
                </code> —
                <a href={targetSuggestion.driver_url} target="_blank" rel="noreferrer" style={{ color: theme.accent }}>
                  Download driver
                </a>
                {formData.targetDriver !== targetSuggestion.driver && (
                  <button
                    type="button"
                    onClick={applyTargetDetected}
                    style={{
                      marginLeft: 8,
                      padding: '2px 8px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: 6,
                      background: theme.cardBackground,
                      cursor: 'pointer',
                      color: theme.text
                    }}
                  >
                    Use detected
                  </button>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Table {required && '*'}
              </label>
              <input
                type="text"
                required={required && targetType === 'jdbc'}
                value={formData.targetTable || ''}
                onChange={(e) => onFormDataChange({ targetTable: e.target.value })}
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                User {required && '*'}
              </label>
              <input
                type="text"
                required={required && targetType === 'jdbc'}
                value={formData.targetUser || ''}
                onChange={(e) => onFormDataChange({ targetUser: e.target.value })}
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Password
            </label>
            <input
              type="password"
              value={formData.targetPassword || ''}
              onChange={(e) => onFormDataChange({ targetPassword: e.target.value })}
              style={{
                width: '100%',
                padding: 8,
                border: `1px solid ${theme.inputBorder}`,
                borderRadius: 6,
                fontSize: 13,
                background: theme.inputBackground,
                color: theme.text
              }}
            />
          </div>
        </>
      )}

      {targetType === 'csv_directory' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                CSV Output Path *
              </label>
              <input
                type="text"
                value={formData.targetCsvPath || ''}
                onChange={(e) => onFormDataChange({ targetCsvPath: e.target.value })}
                placeholder="C:\\output\\csv"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Delimiter
              </label>
              <input
                type="text"
                value={formData.targetCsvDelimiter || ','}
                onChange={(e) => onFormDataChange({ targetCsvDelimiter: e.target.value })}
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text }}>
              <input
                type="checkbox"
                checked={formData.targetCsvHeader !== undefined ? formData.targetCsvHeader : true}
                onChange={(e) => onFormDataChange({ targetCsvHeader: e.target.checked })}
              />
              Include Header
            </label>
          </div>
        </>
      )}

      {targetType === 'parquet_directory' && (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Parquet Output Path *
            </label>
            <input
              type="text"
              value={formData.targetParquetPath || ''}
              onChange={(e) => onFormDataChange({ targetParquetPath: e.target.value })}
              placeholder="C:\\output\\parquet"
              style={{
                width: '100%',
                padding: 8,
                border: `1px solid ${theme.inputBorder}`,
                borderRadius: 6,
                fontSize: 13,
                background: theme.inputBackground,
                color: theme.text
              }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Partition By Column (optional)
            </label>
            <input
              type="text"
              value={formData.targetParquetPartitionBy || ''}
              onChange={(e) => onFormDataChange({ targetParquetPartitionBy: e.target.value })}
              placeholder="e.g., reporting_period"
              style={{
                width: '100%',
                padding: 8,
                border: `1px solid ${theme.inputBorder}`,
                borderRadius: 6,
                fontSize: 13,
                background: theme.inputBackground,
                color: theme.text
              }}
            />
            <div style={{ marginTop: 4, fontSize: 12, color: theme.textSecondary }}>
              Partition parquet files by this column for better query performance
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Re-Partition By Column (optional)
            </label>
            <input
              type="text"
              value={formData.targetParquetRePartitionBy || ''}
              onChange={(e) => onFormDataChange({ targetParquetRePartitionBy: e.target.value })}
              placeholder="e.g., product_id"
              style={{
                width: '100%',
                padding: 8,
                border: `1px solid ${theme.inputBorder}`,
                borderRadius: 6,
                fontSize: 13,
                background: theme.inputBackground,
                color: theme.text
              }}
            />
            <div style={{ marginTop: 4, fontSize: 12, color: theme.textSecondary }}>
              Repartition data by this column before writing for better data distribution
            </div>
          </div>
        </>
      )}

      {targetType === 'excel_directory' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Excel Output Path *
              </label>
              <input
                type="text"
                value={formData.targetExcelPath || ''}
                onChange={(e) => onFormDataChange({ targetExcelPath: e.target.value })}
                placeholder="C:\\output\\excel"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Sheet Name
              </label>
              <input
                type="text"
                value={formData.targetExcelSheet || 'Sheet1'}
                onChange={(e) => onFormDataChange({ targetExcelSheet: e.target.value })}
                placeholder="Sheet1"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Data Address
              </label>
              <input
                type="text"
                value={formData.targetExcelDataAddress || ''}
                onChange={(e) => onFormDataChange({ targetExcelDataAddress: e.target.value })}
                placeholder="A1"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text, marginTop: 28 }}>
                <input
                  type="checkbox"
                  checked={formData.targetExcelHeader !== undefined ? formData.targetExcelHeader : true}
                  onChange={(e) => onFormDataChange({ targetExcelHeader: e.target.checked })}
                />
                Include Header
              </label>
            </div>
          </div>
        </>
      )}

      {targetType === 'azure_blob' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Storage Account Name *
              </label>
              <input
                type="text"
                required={required && targetType === 'azure_blob'}
                value={formData.targetAzureBlobStorageAccount || ''}
                onChange={(e) => onFormDataChange({ targetAzureBlobStorageAccount: e.target.value })}
                placeholder="mystorageaccount"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Container Name *
              </label>
              <input
                type="text"
                required={required && targetType === 'azure_blob'}
                value={formData.targetAzureBlobContainer || ''}
                onChange={(e) => onFormDataChange({ targetAzureBlobContainer: e.target.value })}
                placeholder="mycontainer"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              SAS Token *
            </label>
            <input
              type="password"
              required={required && targetType === 'azure_blob'}
              value={formData.targetAzureBlobSasToken || ''}
              onChange={(e) => onFormDataChange({ targetAzureBlobSasToken: e.target.value })}
              placeholder="sv=2021-06-08&ss=b&srt=sco&sp=rwdlac..."
              style={{
                width: '100%',
                padding: 8,
                border: `1px solid ${theme.inputBorder}`,
                borderRadius: 6,
                fontSize: 13,
                background: theme.inputBackground,
                color: theme.text
              }}
            />
            <div style={{ marginTop: 4, fontSize: 12, color: theme.textSecondary }}>
              Shared Access Signature token for authentication (can be set via environment variable AZURE_STORAGE_SAS_TOKEN)
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Blob Path
              </label>
              <input
                type="text"
                value={formData.targetAzureBlobPath || ''}
                onChange={(e) => onFormDataChange({ targetAzureBlobPath: e.target.value })}
                placeholder="data/output"
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: theme.textSecondary }}>
                Path within the container (optional)
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Format *
              </label>
              <select
                value={formData.targetAzureBlobFormat || 'parquet'}
                onChange={(e) => onFormDataChange({ targetAzureBlobFormat: e.target.value })}
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              >
                <option value="parquet">Parquet</option>
                <option value="csv">CSV</option>
                <option value="json">JSON</option>
                <option value="orc">ORC</option>
                <option value="avro">Avro</option>
                <option value="excel">Excel</option>
              </select>
            </div>
          </div>
          {formData.targetAzureBlobFormat === 'csv' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                  Delimiter
                </label>
                <input
                  type="text"
                  value={formData.targetAzureBlobCsvDelimiter || ','}
                  onChange={(e) => onFormDataChange({ targetAzureBlobCsvDelimiter: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 8,
                    border: `1px solid ${theme.inputBorder}`,
                    borderRadius: 6,
                    fontSize: 13,
                    background: theme.inputBackground,
                    color: theme.text
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text, marginTop: 28 }}>
                  <input
                    type="checkbox"
                    checked={formData.targetAzureBlobCsvHeader !== undefined ? formData.targetAzureBlobCsvHeader : true}
                    onChange={(e) => onFormDataChange({ targetAzureBlobCsvHeader: e.target.checked })}
                  />
                  Include Header
                </label>
              </div>
            </div>
          )}
          {(formData.targetAzureBlobFormat === 'parquet' || formData.targetAzureBlobFormat === 'orc') && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                Compression
              </label>
              <select
                value={formData.targetAzureBlobCompression || 'snappy'}
                onChange={(e) => onFormDataChange({ targetAzureBlobCompression: e.target.value })}
                style={{
                  width: '100%',
                  padding: 8,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: theme.inputBackground,
                  color: theme.text
                }}
              >
                <option value="snappy">Snappy</option>
                <option value="gzip">Gzip</option>
                <option value="lzo">LZO</option>
                <option value="none">None</option>
              </select>
            </div>
          )}
          {formData.targetAzureBlobFormat === 'excel' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                  Sheet Name
                </label>
                <input
                  type="text"
                  value={formData.targetAzureBlobExcelSheet || 'Sheet1'}
                  onChange={(e) => onFormDataChange({ targetAzureBlobExcelSheet: e.target.value })}
                  placeholder="Sheet1"
                  style={{
                    width: '100%',
                    padding: 8,
                    border: `1px solid ${theme.inputBorder}`,
                    borderRadius: 6,
                    fontSize: 13,
                    background: theme.inputBackground,
                    color: theme.text
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
                  Data Address
                </label>
                <input
                  type="text"
                  value={formData.targetAzureBlobExcelDataAddress || ''}
                  onChange={(e) => onFormDataChange({ targetAzureBlobExcelDataAddress: e.target.value })}
                  placeholder="A1 (optional)"
                  style={{
                    width: '100%',
                    padding: 8,
                    border: `1px solid ${theme.inputBorder}`,
                    borderRadius: 6,
                    fontSize: 13,
                    background: theme.inputBackground,
                    color: theme.text
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text, marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={formData.targetAzureBlobExcelHeader !== undefined ? formData.targetAzureBlobExcelHeader : true}
                    onChange={(e) => onFormDataChange({ targetAzureBlobExcelHeader: e.target.checked })}
                  />
                  Include Header
                </label>
              </div>
            </div>
          )}
        </>
      )}

      {/* Write Mode - common for all target types */}
      <div style={{ marginTop: 8 }}>
        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
          Write Mode
        </label>
        <select
          value={formData.mode || 'overwrite'}
          onChange={(e) => onFormDataChange({ mode: e.target.value })}
          style={{
            width: '100%',
            padding: 8,
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: 6,
            fontSize: 13,
            background: theme.inputBackground,
            color: theme.text
          }}
        >
          <option value="overwrite">Overwrite</option>
          <option value="append">Append</option>
        </select>
      </div>
    </div>
  );
};
